<script setup>
import EnTextField from 'Components/inputs/EnTextFieldV2/EnTextFieldV2.vue';
import EnValidationErrorMessage from 'Components/feedback/EnValidationErrorMessage/EnValidationErrorMessage.vue';
import EnOverlaySpinner from 'Components/feedback/EnOverlaySpinner/EnOverlaySpinner';
import EnIcon from 'Components/icons/EnIcon.vue';

import { getUrlParams } from 'Js/utils/urlUtils';
import { required, validEmail, minLength, maxLength } from 'Js/validation/rules';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import { EnButtonType } from 'Components/types/EnButtonType';

const accountName = ref('');
const guid = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const returnUrl = ref('');
const errors = ref([]);
const loading = ref(true);
const successRequest = ref(null);
const form = ref();
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const emailRules = ref([
  required,
  validEmail
]);
const updateRail = inject('updateRail');
const passwordRules = ref([
  required, val => minLength(val, 6), val => maxLength(val, 100)
]);
const confirmPasswordRules = ref([
  required, val => minLength(val, 6), val => maxLength(val, 100), val => val === password.value || t('Authentication.password.reset.passwordsDoNotMatch')
]);
const backToLoginUrl = computed(() => {
  return `/Account/Login?returnUrl=${returnUrl.value}`;
});
const isInvalid = computed(() => {
  return !(form.value && form.value.isValid);
});

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}
function toggleConfirmPasswordVisibility() {
  showConfirmPassword.value = !showConfirmPassword.value;
}
async function tryRegister() {
  loading.value = true;
  const response = await fetch(`/api/Manage/TryRegister`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      Email: email.value,
      Password: password.value,
      ConfirmPassword: confirmPassword.value,
      ReturnUrl: returnUrl.value,
    })
  });
  if (response.ok) {
    const config = await response.json();
    errors.value = config.errors;
    //If there are no errors, then registration is successful. Just open Login page
    if (errors.value.length === 0) {
      window.location.href = returnUrl.value;
    }
    loading.value = false;
  }
}

onBeforeMount(async () => {
  const params = getUrlParams(window.location);
  returnUrl.value = params['returnurl'];

  const additionalParams = new URLSearchParams(returnUrl.value);
  accountName.value = additionalParams.get('a');
  guid.value = additionalParams.get('g');
  
  const response = await fetch(`/api/Manage/GetRegisterInfo?returnUrl=${encodeURIComponent(returnUrl.value)}`, {
    method: 'POST',
  });
  if (response.ok) {
    const config = await response.json();
    if (config.errors.length > 0) {
      // If some errors encountered, we cannot allow user to process registration
      window.location.href = backToLoginUrl.value;
    }
  }

  loading.value = false;
});
onMounted(async () => {
  const main = document.querySelector('main.main-v2');
  main.classList.add('login-page');
  
  updateRail?.(true);
});
</script>

<template>
  <div>
    <v-container fluid class="d-flex justify-center align-center pa-6">
      <en-overlay-spinner v-model="loading"></en-overlay-spinner>
      <v-card>
        <v-card-text class="d-flex flex-column align-center pa-8">
          <div class="en-icon-container">
            <en-icon
                size="30"
                class="en-icon icon-32 icon-enloc_logo_min"
            ></en-icon>
          </div>
          <div class="mb-6 en-title">
            {{ t('Authentication.register.subtitle') }}
          </div>
          <template v-if="errors">
            <en-validation-error-message v-for="error in errors" :error="error"></en-validation-error-message>
          </template>

          <v-form ref="form" method="post" class="w-100" @submit.prevent="tryRegister">
            <div class="mt-7 mb-3 w-100">
              <div class="label">{{ t('Authentication.register.email') }}</div>
              <en-text-field
                  v-model="email"
                  id="form-input-username"
                  variant="outlined"
                  rounded="lg"
                  type="email"
                  density="compact"
                  name="EmailOrUsername"
                  :rules="emailRules"
              ></en-text-field>
            </div>
            <div class="mt-7 mb-3 w-100">
              <div class="label">{{ t('Authentication.register.password') }}</div>
              <en-text-field
                  v-model="password"
                  id="form-input-password"
                  variant="outlined"
                  rounded="lg"
                  :type="showPassword ? 'text' : 'password'"
                  :suffix-icon-class="password ? (showPassword ? 'mdi-eye-off' : 'mdi-eye') : null"
                  :on-suffix-icon-click="togglePasswordVisibility"
                  density="compact"
                  name="Password"
                  :rules="passwordRules"
              ></en-text-field>
            </div>

            <div class="w-100">
              <div class="label">{{ t('Authentication.register.confirmPassword') }}</div>
              <en-text-field
                  id="form-input-confirm-password"
                  name="ConfirmPassword"
                  variant="outlined"
                  rounded="lg"
                  density="compact"
                  v-model="confirmPassword"
                  :rules="confirmPasswordRules"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :suffix-icon-class="password ? (showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye') : null"
                  :on-suffix-icon-click="toggleConfirmPasswordVisibility"
              ></en-text-field>
            </div>
            <input name="buttonValue" type="hidden" value="login"/>

            <en-button
                static-id="form-input-submit-button"
                :type="EnButtonType.PRIMARY"
                native-type="submit"
                class="button-tex mt-3"
                :loading="loading"
                :disabled="isInvalid"
            >
              {{ t('Authentication.login.buttonText') }}
            </en-button>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<style scoped lang="scss">
@use "Styles/helper/variables.scss";

.en-icon {
  color: variables.$en-red !important;
}

.v-card {
  width: 450px;
  border: 1.5px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: none !important;
  border-radius: 12px;
}

:deep(.v-input__details) {
  display: none !important;
}

.label {
  display: block;
  font-size: 0.875rem;
  color: variables.$en-blue !important;
}

.justify-end {
  margin-bottom: 24px;
}

.v-btn {
  border-radius: 8px !important;
}

.v-btn.mb-6 {
  margin-bottom: 24px;
}

.v-card-actions {
  padding-top: 24px;
  padding-bottom: 24px;
  justify-content: center;
  min-height: auto;
}

.en-icon-container {
  border: 1px solid variables.$login-page-icon-container-border;
  border-radius: 12px;
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
}

.en-title {
  font-size: 1.5rem;
  font-weight: 500 !important;
  margin-top: 10px;
  color: variables.$en-blue !important;
}
</style>